import Cart from "@bevco/MobiusOverrides/Icons/Cart";
import customGetSnapShotData from "@bevco/Store/Handlers/MyAccount/GetSnapshotData";
import { colorPalette, colorStrings, mediaQueriesBP } from "@bevco/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Box from "@insite/mobius/Icons/Box";
import File from "@insite/mobius/Icons/File";
import Img from "@insite/mobius/Img";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";

import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapDispatchToProps = {
    customGetSnapShotData,
};

export type Props = ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface TopPurchasedStyles {
    title?: TypographyProps;
    container?: GridContainerProps;
    purchasedItem?: GridItemProps;
    imageWrapper?: InjectableCss;
    productName?: TypographyProps;
    productInfo?: TypographyProps;
    productLink?: LinkProps;
}

export const topPurchasedStyles: TopPurchasedStyles = {
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 1rem;
        `,
    },
    container: {
        gap: 0,
        css: css`
            & > div {
                display: grid !important;
                gap: 24px;
                grid-template-columns: repeat(5, 1fr);
                margin: 0;
                padding: 0;
                width: 100%;

                ${mediaQueriesBP.maxLG} {
                    grid-template-columns: 1fr;
                }
            }
        `,
    },

    purchasedItem: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            border: 1px solid ${getColor(colorStrings.commonGlobalGray03)};
            position: relative;
            padding: 16px;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            max-width: unset;
            text-align: left;
            align-items: flex-start;

            ${mediaQueriesBP.maxLG} {
                padding: 8px;
                flex-direction: row;
                justify-content: flex-start;
                align-items: flex-start;
            }
        `,
    },

    imageWrapper: {
        css: css`
            width: 100%;
            height: 100px;
            margin-bottom: 16px;

            img {
                width: 100%;
                height: 100%;
                object-fit: contain;
            }

            ${mediaQueriesBP.maxLG} {
                width: 30%;
                height: 80px;
                max-width: 80px;
                margin-right: 8px;
                margin-top: 8px;
            }
        `,
    },

    productName: {
        size: 16,
        weight: "bold",
        css: css`
            margin-bottom: 8px;
            width: 100%;
            text-align: left;
            line-height: 1.2;
            display: block;
        `,
    },

    productInfo: {
        variant: "p",
        size: 14,
        css: css`
            margin: 5px 0;
            width: 100%;
            text-align: left;
            line-height: 1.3;
            display: flex;
            align-items: center;

            .label {
                font-weight: bold;
                margin-right: 5px;
            }
        `,
    },

    productLink: {
        typographyProps: {
            size: 14,
        },
        css: css`
            display: inline;
            line-height: 1.3;
            margin: 0;
            span,
            a {
                display: inline;
                line-height: 1.3;
            }
        `,
    },
};

export const styles = topPurchasedStyles;

export const TopPurchasedWidget = ({ customGetSnapShotData, fields }: Props) => {
    const [topPurchasedInfo, setTopPurchasedInfo] = useState([]) as any;
    useEffect(() => {
        customGetSnapShotData({
            onSuccess: result => {},
            onComplete: result => {
                if (result.apiResult) {
                    setTopPurchasedInfo(result.apiResult.TopPurchasedItems);
                }
            },
        });
    }, []);

    if (topPurchasedInfo.length === 0) {
        return;
    }

    return (
        <>
            <Typography {...styles.title}>{fields.title}</Typography>

            <GridContainer {...styles.container}>
                {topPurchasedInfo.map((item: any, index: number) => (
                    <GridItem key={index} {...styles.purchasedItem}>
                        <StyledWrapper {...styles.imageWrapper}>
                            <Img src={item.ProductImage} />
                        </StyledWrapper>
                        <StyledWrapper>
                            {item.Brand && item.BrandUrl && (
                                <Link {...styles.productLink} href={item.BrandUrl}>
                                    {item.Brand}
                                </Link>
                            )}
                            {item.Brand && !item.BrandUrl && (
                                <Typography {...styles.productInfo}>{item.Brand}</Typography>
                            )}
                            {item.ProductDescription && (
                                <Typography {...styles.productName}>{item.ProductDescription}</Typography>
                            )}
                            {item.CustomerProductNumber && (
                                <Typography {...styles.productInfo}>
                                    <span className="label">{translate("Customer Part #")}: </span>
                                    <span>{item.CustomerProductNumber}</span>
                                </Typography>
                            )}
                            {item.ErpNumber && (
                                <Typography {...styles.productInfo}>
                                    <span className="label">{siteMessage("BEVCO_Part_#")}: </span>
                                    <Link {...styles.productLink} href={item.UrlSegment}>
                                        {/* remove leading zeros from ERP number */}
                                        {item.ErpNumber.replace(/^0+/, "")}
                                    </Link>
                                </Typography>
                            )}
                            {item.ManufacturerItem && (
                                <Typography {...styles.productInfo}>
                                    <span className="label">{translate("Manufacturer Part #")}: </span>
                                    <span>{item.ManufacturerItem}</span>
                                </Typography>
                            )}
                            {!item.CustomerProductNumber && item.Manufacturer && (
                                <Typography {...styles.productInfo}>
                                    <span className="label">{translate("Manufacturer")}: </span>
                                    <span>{item.Manufacturer}</span>
                                </Typography>
                            )}
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(TopPurchasedWidget as any) as any,
    definition: {
        displayName: "Top Purchased",
        group: "Verndale Box" as any,
        icon: "puzzle-piece",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Top Purchased",
                fieldType: "Translatable",
                sortOrder: 1,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
